<template>
  <g ref="pieChart" :transform="translate">
    <path class="label-path" :d="labelPath" :id="$id('label-path')" />
    <text 
      ref="label"
    >
      <textPath 
        ref="textPath"
        :href="$idRef('label-path')"
        class="label-text"
        startOffset="50%"
      />
    </text>
    <path class="green-fill slice" :d="greenPath" />
    <path class="yellow-fill slice" :d="yellowPath" />
    <path class="red-fill slice" :d="redPath" />
    <path class="blue-fill slice" :d="bluePath" />
    <circle class="overlay" :r="chartRadius" />
    <ellipse
      class="glare"
      :rx="chartRadius * 0.3"
      :ry="chartRadius * 0.15"
      :transform="`rotate(-45) translate(0, ${-1 * chartRadius * 0.8})`"
    />
    <foreignObject
      v-if="toolbar"
      :width="40"
      :height="fontSize"
      :x="-20"
      :y="this.chartRadius" 
    >
      <v-row class="toolbar ma-0 pa-0">
          <v-icon small @click.stop="onClose()">fa-times</v-icon>
          <v-spacer />
          <v-icon small>fa-question</v-icon>
      </v-row>
    </foreignObject>
    <radialGradient id="color0" cx="50%" cy="50%" fx="20%" fy="20%">
      <stop offset="0%" stop-color="#ffffdd" stop-opacity="0.0"></stop>
      <stop offset="80%" stop-color="#552255" stop-opacity="0.2"></stop>
      <stop offset="100%" stop-color="#000" stop-opacity="0.4"></stop>
    </radialGradient>
  </g>
</template>

<script>
import * as d3 from "d3";

export default {
  name: "PieChart",
  data() {
    return {
      labelText: "",
      slices: d3.pie().sort(null)([
        this.profile.green,
        this.profile.yellow,
        this.profile.red,
        this.profile.blue,
      ]),
    };
  },
  props: {
    profile: Object,
    position: {
      type: Object,
      default: () => {
        return { x: 0, y: 0 };
      },
    },
    radius: {
      type: Number,
      default: 50,
    },
    fontSize: {
      type: Number,
      default: 15,
    },
    toolbar: {
      type: Boolean,
      default: true,
    },
  },
  watch: {
    radius() {
      this.truncateLabel();
    },
  },
  computed: {
    chartRadius() {
      return this.radius - this.fontSize - 2;
    },
    translate() {
      return `translate(${this.position.x}, ${this.position.y})`;
    },
    labelPath() {
      return d3
        .arc()
        .innerRadius(0)
        .outerRadius(this.chartRadius + 5)
        .startAngle(Math.PI)
        .endAngle(Math.PI * 3)();
    },
    maxLabelLength() {
      return Math.PI * this.chartRadius * 2 * 0.8;
    },
    greenPath() {
      return this.arc(this.slices[0]);
    },
    yellowPath() {
      return this.arc(this.slices[1]);
    },
    redPath() {
      return this.arc(this.slices[2]);
    },
    bluePath() {
      return this.arc(this.slices[3]);
    },
  },
  methods: {
    async truncateLabel() {
      this.$refs.textPath.textContent = this.profile.unitGroup;
      if ((await this.getLabelLength()) > this.maxLabelLength) {
        while ((await this.getLabelLength()) > this.maxLabelLength) {
          this.$refs.textPath.textContent = this.$refs.textPath.textContent.slice(
            0,
            -1
          );
        }
        this.$refs.textPath.textContent =
          this.$refs.textPath.textContent.slice(0, -3) + "...";
      }
    },
    async getLabelLength() {
      return new Promise((res) => {
        if (
          this.$refs.textPath &&
          this.$refs.textPath.getComputedTextLength() > 0
        ) {
          return res(this.$refs.textPath.getComputedTextLength());
        } else {
          setInterval(() => {
            if (
              this.$refs.textPath &&
              this.$refs.textPath.getComputedTextLength() > 0
            ) {
              return res(this.$refs.textPath.getComputedTextLength());
            }
          }, 1);
        }
      });
    },
    arc(data) {
      return d3.arc().innerRadius(0).outerRadius(this.chartRadius)(data);
    },
    onClose() {
      const duration = 250;
      const pieChart = d3.select(this.$refs.pieChart);

      pieChart
        .selectAll(".slice")
        .transition()
        .duration(duration)
        .attr("d", (_, i) => {
          return d3.arc().innerRadius(0).outerRadius(0.01)(this.slices[i]);
        });

      pieChart
        .select(".overlay")
        .transition()
        .duration(duration)
        .attr("r", 0.01);

      pieChart
        .select(".label-path")
        .transition()
        .duration(duration)
        .attr(
          "d",
          d3
            .arc()
            .startAngle(Math.PI)
            .endAngle(Math.PI * 3)
            .innerRadius(0)
            .outerRadius(0.01)()
        );

      pieChart
        .select(".label-text")
        .transition()
        .duration(duration)
        .style("font-size", "0.01px");

      setTimeout(() => {
        this.$emit("close", this.profile);
      }, duration);
    },
  },
  mounted() {
    this.truncateLabel();
  },
};
</script>

<style scoped>
.label-path {
  fill: none;
  stroke: none;
  text-anchor: middle;
}
.label-text {
  text-anchor: middle;
  fill: #212121;
}

.slice {
  stroke: black;
  stroke-width: 1px;
}

div.toolbar {
  width: 100%;
  height: 100%;
  position: fixed;
}

.overlay {
  fill: url("#color0");
}

.glare {
  stroke: none;
  fill: white;
  fill-opacity: 0.3;
}
</style>
